import InfoHelp from "../components/help/InfoHelp";
import { metricHelp } from "../components/help/helpText";
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../api/client";
import Header from "../components/layout/Header";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import { formatBRL, formatPct, formatDateTimeBR } from "../utils/format";
import {
  AlertTriangle, ArrowRight, Award, Building2, CalendarClock, CheckCircle2,
  ClipboardCheck, FileText, RefreshCcw, Target, TrendingUp, Users,
} from "lucide-react";

function ActionCard({ icon: Icon, title, description, to, button, primary = false }) {
  return (
    <Card className={primary ? "border-primary/25 bg-primary/[0.045]" : "border-border/75"}>
      <CardContent className="flex h-full flex-col p-5 sm:p-6">
        <div className="grid size-11 place-items-center rounded-xl bg-primary/10 text-primary">
          <Icon className="size-5" />
        </div>
        <h2 className="mt-4 text-lg font-semibold tracking-[-0.02em]">{title}</h2>
        <p className="mt-1 min-h-10 text-sm leading-relaxed text-muted-foreground">{description}</p>
        <div className="mt-5">
          <Link to={to}><Button className="w-full sm:w-auto" variant={primary ? "default" : "outline"}>{button}</Button></Link>
        </div>
      </CardContent>
    </Card>
  );
}

function NumberCard({ label, value, help, icon: Icon, testid }) {
  return (
    <Card className="min-w-0 overflow-hidden border-border/75" data-testid={testid}>
      <CardContent className="min-w-0 p-4 sm:p-5">
        <div className="flex min-w-0 items-start gap-3">
          <div className="min-w-0 flex-1 overflow-hidden">
            <div className="text-sm font-medium leading-snug text-muted-foreground">{label}<InfoHelp label={label}>{metricHelp[label] || help}</InfoHelp></div>
            <div className="mt-2 break-words font-mono text-[clamp(1.15rem,2vw,1.65rem)] font-semibold leading-tight tabular-nums tracking-[-0.03em] text-foreground">
              {value}
            </div>
            {help && <div className="mt-2 text-sm leading-relaxed text-muted-foreground">{help}</div>}
          </div>
          <div className="grid size-9 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary"><Icon className="size-4" /></div>
        </div>
      </CardContent>
    </Card>
  );
}

function SectionTitle({ title, description }) {
  return (
    <div className="mb-4">
      <h2 className="text-xl font-semibold tracking-[-0.025em]">{title}</h2>
      {description && <p className="mt-1 max-w-3xl text-sm text-muted-foreground">{description}</p>}
    </div>
  );
}

function AttentionItem({ icon: Icon, title, detail, count, to }) {
  return (
    <Link to={to} className="group flex items-center gap-3 rounded-xl border border-border/70 bg-background/55 p-3.5 transition hover:border-primary/25 hover:bg-muted/30">
      <div className="grid size-10 shrink-0 place-items-center rounded-xl bg-amber-500/10 text-amber-600 dark:text-amber-400">
        <Icon className="size-4" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-semibold">{title}</span>
          <span className="inline-flex min-w-7 justify-center rounded-full bg-amber-500/12 px-2 py-0.5 font-mono text-xs font-semibold text-amber-700 dark:text-amber-300">{count}</span>
        </div>
        <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">{detail}</p>
      </div>
      <ArrowRight className="size-4 shrink-0 text-muted-foreground transition group-hover:translate-x-0.5 group-hover:text-foreground" />
    </Link>
  );
}

export default function Dashboard() {
  const { isAdmin, userInfo } = useAuth();
  const [data, setData] = useState(null);
  const [operations, setOperations] = useState(null);
  const [lifecycle, setLifecycle] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    Promise.allSettled([
      api.get("/dashboard"),
      api.get("/operations/overview"),
      api.get("/commercial-lifecycle"),
    ]).then(([dash, ops, commercial]) => {
      if (dash.status === "fulfilled") setData(dash.value.data);
      if (ops.status === "fulfilled") setOperations(ops.value.data);
      if (commercial.status === "fulfilled") setLifecycle(commercial.value.data);
    }).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const firstName = useMemo(() => String(userInfo?.nome || "").trim().split(/\s+/)[0], [userInfo]);
  const pendingAnnual = lifecycle?.counts?.pendente || 0;
  const technicalPending = (operations?.deadlines?.vencida || 0) + (operations?.evidence_pending || 0) + (operations?.tasks_pending || 0);
  const trainingsExpiring = operations?.trainings_expiring_30d || 0;
  const proposalsExpiring = data?.propostas_vencendo?.length || 0;

  const attentionItems = [
    trainingsExpiring > 0 && {
      icon: Award,
      title: "Treinamentos próximos do vencimento",
      detail: "Confira participantes e clientes que precisam de renovação nos próximos 30 dias.",
      count: trainingsExpiring,
      to: "/treinamentos/vencimentos",
    },
    proposalsExpiring > 0 && {
      icon: CalendarClock,
      title: "Propostas vencendo ou vencidas",
      detail: "Revise validade e próximo contato comercial antes de perder a oportunidade.",
      count: proposalsExpiring,
      to: "/propostas",
    },
    pendingAnnual > 0 && {
      icon: Target,
      title: "Clientes para renovar ou recontatar",
      detail: "Acompanhamentos anuais que já podem exigir uma nova abordagem comercial.",
      count: pendingAnnual,
      to: "/propostas",
    },
    technicalPending > 0 && {
      icon: AlertTriangle,
      title: "Pendências técnicas",
      detail: "Planos de ação, evidências ou tarefas internas que ainda precisam de tratamento.",
      count: technicalPending,
      to: "/operacoes/hoje",
    },
  ].filter(Boolean);

  return (
    <>
      <Header
        title="Início"
        subtitle={firstName ? `Olá, ${firstName}. Escolha o que você precisa fazer.` : "Escolha o que você precisa fazer."}
        actions={<Button variant="outline" onClick={load} className="hidden sm:inline-flex"><RefreshCcw className="size-4" /> Atualizar</Button>}
      />

      <div className="page-container space-y-8" data-testid="dashboard-page">
        <section>
          <SectionTitle title="O que você quer fazer?" description="As tarefas mais usadas ficam sempre no topo." />
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <ActionCard primary icon={FileText} title="Criar proposta" description="Monte uma nova proposta comercial para um cliente." to="/propostas/nova" button="Nova proposta" />
            <ActionCard primary icon={Award} title="Emitir certificados" description="Gere certificados e listas de presença de um treinamento." to="/treinamentos" button="Emitir certificados" />
            <ActionCard icon={ClipboardCheck} title="Acompanhar propostas" description="Veja negociações, revisões, aceitações e próximos contatos." to="/propostas" button="Abrir propostas" />
            <ActionCard icon={CalendarClock} title="Ver vencimentos" description="Confira treinamentos que precisam de renovação." to="/treinamentos/vencimentos" button="Ver treinamentos" />
          </div>
        </section>

        <section>
          <SectionTitle title="Precisa de atenção" description="Só mostramos aqui o que realmente pode exigir uma ação agora." />
          {loading ? (
            <div className="h-28 animate-pulse rounded-2xl border bg-card/50" />
          ) : attentionItems.length > 0 ? (
            <Card className="border-border/75">
              <CardContent className="grid gap-3 p-4 md:grid-cols-2 sm:p-5">
                {attentionItems.map((item) => <AttentionItem key={item.title} {...item} />)}
              </CardContent>
            </Card>
          ) : (
            <Card className="border-emerald-500/20 bg-emerald-500/[0.035]">
              <CardContent className="flex items-center gap-3 p-5">
                <div className="grid size-10 shrink-0 place-items-center rounded-xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"><CheckCircle2 className="size-5" /></div>
                <div>
                  <div className="font-semibold">Nenhuma pendência urgente agora</div>
                  <div className="mt-0.5 text-sm text-muted-foreground">Você pode seguir com propostas, treinamentos ou consultar os demais acompanhamentos quando precisar.</div>
                </div>
              </CardContent>
            </Card>
          )}
          <div className="mt-3 flex flex-wrap gap-2">
            <Link to="/operacoes/hoje"><Button variant="ghost" size="sm">Ver acompanhamento completo</Button></Link>
          </div>
        </section>

        {!loading && data?.recent_proposals?.length > 0 && (
          <section>
            <SectionTitle title="Últimas propostas" description="Retome rapidamente os trabalhos comerciais mais recentes." />
            <Card className="border-border/75">
              <CardContent className="p-3 sm:p-4">
                <div className="divide-y divide-border/70">
                  {data.recent_proposals.slice(0, 5).map((p) => (
                    <Link key={p.id} to={`/propostas/${p.id}`} className="flex flex-col gap-2 rounded-lg px-3 py-3 transition hover:bg-muted/30 sm:flex-row sm:items-center sm:justify-between" data-testid={`recent-proposal-${p.id}`}>
                      <div className="min-w-0">
                        <div className="truncate text-sm font-medium">{p.client_name || "Cliente"}</div>
                        <div className="mt-0.5 text-xs text-muted-foreground">{formatDateTimeBR(p.created_at)} · {p.status || "rascunho"}</div>
                      </div>
                      {isAdmin && <div className="shrink-0 whitespace-nowrap font-mono text-sm font-medium tabular-nums">{formatBRL(p.valor_total || 0)}</div>}
                    </Link>
                  ))}
                </div>
                <div className="mt-3 flex justify-end"><Link to="/propostas"><Button variant="outline" size="sm">Ver todas as propostas</Button></Link></div>
              </CardContent>
            </Card>
          </section>
        )}

        {isAdmin && !loading && data && (
          <section>
            <SectionTitle title="Resumo comercial" description="Indicadores financeiros ficam separados das tarefas do dia a dia." />
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
              <NumberCard testid="stat-faturamento" icon={CheckCircle2} label="Faturamento fechado" value={formatBRL(data.faturamento_fechado || 0)} help={`${formatBRL(data.faturamento_fechado_mes || 0)} neste mês`} />
              <NumberCard testid="stat-conversao" icon={Target} label="Taxa de conversão" value={formatPct(data.taxa_conversao_pct || 0)} help={`${data.propostas_aceitas || 0} proposta(s) aceita(s)`} />
              <NumberCard testid="stat-ticket" icon={TrendingUp} label="Ticket médio" value={formatBRL(data.ticket_medio || 0)} help="Média das propostas aceitas" />
              <NumberCard testid="stat-clientes" icon={Building2} label="Clientes cadastrados" value={data.clientes_total || 0} help={`${lifecycle?.recurring_portfolio_clients || 0} em acompanhamento anual`} />
            </div>
            <div className="mt-3"><Link to="/clientes"><Button variant="ghost" size="sm"><Users className="size-4" /> Ver clientes</Button></Link></div>
          </section>
        )}
      </div>
    </>
  );
}

